import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { UHCProviderLink } from '@src/common/UHCProviderLink';
import { config } from '@uhg-abyss/web/tools/config';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';

import { UHCLink } from '@src/common/UHCLink';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { useUser } from '@src/state/user';
import { useTIN } from '@src/state/taxInformation';
import { getOrgType } from '@src/common/helpers';
import { useOnloading } from '@src/state/landing';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginTop: '40px',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const SaveDescription = styled('p', {
  fontSize: '16px',
  fontWeight: 400,
  fontFamily: '$primary',
  marginBottom: '0px',
});
const AdditionalText = styled('p', {
  fontWeight: 600,
});

export const VerificationPending = () => {
  const { TINNumber } = useTIN.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();
  const orgType = getOrgType(currentFlow);

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      adobeTaggingEvent('TIN_REGISTRATION_COMPLETION', {
        jobName: `tin registration-${orgType}`,
        orgType,
        jobFunctions: '',
        portalTaskAccess: '',
        providerUuid: userUUID,
        registrationTaxId: TINNumber,
      });
      adobeTaggingEvent('TIN_REGISTRATION_FINAL_COMPLETE', {
        jobName: `tin registration-${orgType}`,
        orgType,
        jobFunctions: '',
        portalTaskAccess: '',
        providerUuid: userUUID,
        registrationTaxId: TINNumber,
      });
      adobeTaggingEvent('PAA_REGISTRATION_COMPLETION', {
        jobName: 'paa registration',
        companyVerificationMethod: 'w9 form',
        providerUuid: userUUID,
        registrationTaxId: TINNumber,
      });
      adobeTaggingEvent('PAA_REGISTRATION_FINAL_COMPLETE', {
        jobName: 'paa registration',
        companyVerificationMethod: 'w9 form',
        providerUuid: userUUID,
        registrationTaxId: TINNumber,
      });
    }
    document.title = isAPIMarketPlaceUser
      ? 'Company Verification | API Marketplace'
      : 'Company Verification | UHC Provider Portal';
  }, []);

  const linkLabel = isAPIMarketPlaceUser
    ? 'Continue to API Marketplace'
    : 'Return to Provider Portal';

  const getLinkTargetURL = () => {
    return isAPIMarketPlaceUser
      ? uiMessages.getRow('API_MARKETPLACE_WELCOME_URL')?.message ||
          config('API_MARKETPLACE_WELCOME_URL')
      : uiMessages.getRow('SDE_URL')?.message || config('SDE_URL');
  };

  return (
    <React.Fragment>
      <MainContainer>
        <IconBrand
          icon="clock"
          style={{ width: '72px', height: '72px' }}
          variant="twotone"
        />
        <BoxHeader>
          <BoxHeaderText>
            <span>
              Please allow at least 48 hours for us to verify your organization
              information.
            </span>
          </BoxHeaderText>
        </BoxHeader>
        <SaveDescription>
          We'll send you an email once your company has been verified with the
          IRS.{' '}
          {isAPIMarketPlaceUser &&
            ' Once authenticated, an API Consultant will reach out in the following 2 business days.'}
        </SaveDescription>
        {!isAPIMarketPlaceUser && (
          <div>
            <AdditionalText>
              In the interim, review our helpful resources for Primary Access
              Administrators below.
            </AdditionalText>
            <Layout.Stack alignItems="left">
              <Link
                href={config('CREATE_MANAGE_USERS_GUIDE')}
                onClick={() => {
                  adobeTaggingEvent('EXIT_LINK_CLICK', {
                    exitLink: 'how to create and manage users',
                    exitUrl: config('CREATE_MANAGE_USERS_GUIDE'),
                    location: 'company verification - verification pending',
                  });
                }}
              >
                How to create and manage users
              </Link>
              <Link
                href={config('GUIDE_FOR_RCM_BILLING_VENDOR')}
                onClick={() => {
                  adobeTaggingEvent('EXIT_LINK_CLICK', {
                    exitLink:
                      'guide for rcm, billing, and vendor organizations',
                    exitUrl: config('GUIDE_FOR_RCM_BILLING_VENDOR'),
                    location: 'company verification - verification pending',
                  });
                }}
              >
                Guide for RCM, billing, and vendor organizations
              </Link>
              <Link
                href={config('THIRD_PARTY_GROUP_ACCESS_GUIDE')}
                onClick={() => {
                  adobeTaggingEvent('EXIT_LINK_CLICK', {
                    exitLink: '3rd party access guide',
                    exitUrl: config('THIRD_PARTY_GROUP_ACCESS_GUIDE'),
                    location: 'company verification - verification pending',
                  });
                }}
              >
                3rd Party Access guide
              </Link>
            </Layout.Stack>
          </div>
        )}
      </MainContainer>
      {!isAPIMarketPlaceUser && (
        <UHCProviderLink
          style={{
            margin: 'auto',
            display: 'inline-flex',
            marginTop: '20px',
            flexDirection: 'row',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        />
      )}
      <br />
      <UHCLink
        label={linkLabel}
        url={getLinkTargetURL()}
        sameTab={isAPIMarketPlaceUser}
      />
    </React.Fragment>
  );
};
